import { CheckCircle2, ChevronRight, History } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { PureDate } from '@/lib/absolute-datetime'
import { TimesheetsService } from '@/services/timesheets'
import type { HourType } from '@/types/hour-type'
import type { Project } from '@/types/project'
import type { Timesheet } from '@/types/timesheet'
import { DeleteConfirmDialog } from './DeleteConfirmDialog'
import { SearchableSelectDialog } from './SearchableSelectDialog'

interface TimesheetLineEditDialogProps {
	timesheet: Timesheet
	line: Timesheet['lines'][0]
	open: boolean
	onOpenChange: (open: boolean) => void
	hourTypes: HourType[]
	projects: Project[]
	onUpdate: (updated: Timesheet) => void
	onDelete: (updated: Timesheet) => void
}

export function TimesheetLineEditDialog({
	timesheet,
	line,
	open,
	onOpenChange,
	hourTypes,
	projects,
	onUpdate,
	onDelete,
}: TimesheetLineEditDialogProps) {
	// Validación defensiva
	if (!line || !timesheet) {
		return null
	}

	const [start, setStart] = useState(() => {
		try {
			return line?.start ? new PureDate(line.start).format('HH:mm') : ''
		} catch {
			return ''
		}
	})
	const [end, setEnd] = useState(() => {
		try {
			return line?.end ? new PureDate(line.end).format('HH:mm') : ''
		} catch {
			return ''
		}
	})
	const [hourTypeID, setHourTypeID] = useState(() => line?.hourType?.id || '')
	const [projectID, setProjectID] = useState(() => line?.project?.id || '')
	const [text, setText] = useState(line?.text || '')
	const [saving, setSaving] = useState(false)
	const [deleting, setDeleting] = useState(false)
	const [approving, setApproving] = useState(false)
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
	const [showHistoryDialog, setShowHistoryDialog] = useState(false)
	const [showHourTypeSearch, setShowHourTypeSearch] = useState(false)
	const [showProjectSearch, setShowProjectSearch] = useState(false)
	const isApproved = line?.approved ?? false

	const selectedHourType = hourTypes.find((h) => h.id === hourTypeID)
	const selectedProject = projects.find((p) => p.id === projectID)

	// Sincronizar el estado cuando cambie la línea
	useEffect(() => {
		if (open && line) {
			if (line.start) {
				setStart(new PureDate(line.start).format('HH:mm'))
			}
			if (line.end) {
				setEnd(new PureDate(line.end).format('HH:mm'))
			}
			if (line.hourType?.id) {
				setHourTypeID(line.hourType.id)
			}
			if (line.project?.id) {
				setProjectID(line.project.id)
			}
			setText(line.text || '')
		}
	}, [line, open])

	const handleSave = async () => {
		try {
			setSaving(true)

			if (!line?.start || !line?.end) {
				throw new Error('Datos de la línea incompletos')
			}

			// Convertir las horas HH:mm a fechas completas usando la fecha original
			const originalStartDate = new PureDate(line.start)
			const originalEndDate = new PureDate(line.end)

			const [startHoursStr, startMinutesStr] = start.split(':')
			const [endHoursStr, endMinutesStr] = end.split(':')

			if (!startHoursStr || !startMinutesStr || !endHoursStr || !endMinutesStr) {
				throw new Error('Formato de hora inválido')
			}

			const startHours = Number(startHoursStr)
			const startMinutes = Number(startMinutesStr)
			const endHours = Number(endHoursStr)
			const endMinutes = Number(endMinutesStr)

			const updatedStartDate = originalStartDate.setTime(startHours, startMinutes)
			const updatedEndDate = originalEndDate.setTime(endHours, endMinutes)

			// Validar que la hora de inicio sea anterior a la hora de fin
			const comparison = updatedStartDate.compare(updatedEndDate)
			if (comparison >= 0) {
				throw new Error('La hora de inicio debe ser anterior a la hora de fin')
			}

			if (!line?.id || !timesheet?.company?.id || !timesheet?.id) {
				throw new Error('Datos incompletos para actualizar la línea')
			}

			const updated = await TimesheetsService.updateTimesheetLine({
				id: line.id,
				companyID: timesheet.company.id,
				timesheetID: timesheet.id,
				hourTypeID,
				projectID,
				start: updatedStartDate.toString(),
				end: updatedEndDate.toString(),
				text: text.trim() || undefined,
			})

			onUpdate(updated)
			onOpenChange(false)
			toast.success('Línea actualizada correctamente')
		} catch (err) {
			console.error(err)
			toast.error(err instanceof Error ? err.message : 'Error al actualizar la línea')
		} finally {
			setSaving(false)
		}
	}

	const handleDelete = async () => {
		try {
			setDeleting(true)
			setShowDeleteConfirm(false)

			if (!line?.id || !timesheet?.id || !timesheet?.company?.id) {
				throw new Error('Datos incompletos para eliminar la línea')
			}

			const updated = await TimesheetsService.deleteTimesheetLine(line.id, timesheet.id, timesheet.company.id)

			onDelete(updated)
			onOpenChange(false)
			toast.success('Línea eliminada correctamente')
		} catch (err) {
			console.error(err)
			toast.error(err instanceof Error ? err.message : 'Error al eliminar la línea')
		} finally {
			setDeleting(false)
		}
	}

	const handleApprove = async () => {
		try {
			setApproving(true)

			if (!line?.id) {
				throw new Error('ID de línea no válido')
			}

			const updated = await TimesheetsService.approveTimesheetLine(line.id)

			onUpdate(updated)
			toast.success('Línea aprobada correctamente')
		} catch (err) {
			console.error(err)
			toast.error(err instanceof Error ? err.message : 'Error al aprobar la línea')
		} finally {
			setApproving(false)
		}
	}

	const isDisabled = saving || deleting || isApproved

	return (
		<>
			<Dialog open={open} onOpenChange={onOpenChange}>
				<DialogContent className="max-w-md max-h-[90vh] flex flex-col">
					<DialogHeader>
						<div className="flex items-center gap-2">
							<DialogTitle>Editar línea</DialogTitle>
							{isApproved ? (
								<span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 text-xs font-medium">
									<CheckCircle2 className="h-3 w-3" />
									Aprobada
								</span>
							) : (
								''
							)}
						</div>
						<DialogDescription>Modifica los datos de la línea del registro horario</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 py-4 overflow-y-auto flex-1 min-h-0">
						<div className="grid grid-cols-3 gap-4">
							<div className="space-y-2">
								<Label htmlFor="start">Inicio</Label>
								<Input
									id="start"
									type="time"
									value={start}
									onChange={(e) => setStart(e.target.value)}
									disabled={isDisabled}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="end">Fin</Label>
								<Input
									id="end"
									type="time"
									value={end}
									onChange={(e) => setEnd(e.target.value)}
									disabled={isDisabled}
								/>
							</div>
							<div className="space-y-2 flex justify-around flex-col">
								<Label>Duración</Label>
								<p className="text-sm font-medium">
									{(() => {
										try {
											if (!line?.start || !line?.end) return '-'
											const originalStartDate = new PureDate(line.start)
											const originalEndDate = new PureDate(line.end)
											const startParts = start.split(':')
											const endParts = end.split(':')

											if (startParts.length < 2 || endParts.length < 2) {
												return '-'
											}

											const startHours = Number(startParts[0])
											const startMinutes = Number(startParts[1])
											const endHours = Number(endParts[0])
											const endMinutes = Number(endParts[1])

											if (
												isNaN(startHours) ||
												isNaN(startMinutes) ||
												isNaN(endHours) ||
												isNaN(endMinutes)
											) {
												return '-'
											}

											const startDate = originalStartDate.setTime(startHours, startMinutes)
											const endDate = originalEndDate.setTime(endHours, endMinutes)
											const diffMinutes = startDate.diffMinutes(endDate)
											const hours = Math.floor(diffMinutes / 60)
											const minutes = diffMinutes % 60
											if (hours === 0) return `${minutes}m`
											if (minutes === 0) return `${hours}h`
											return `${hours}h ${minutes}m`
										} catch {
											return '-'
										}
									})()}
								</p>
							</div>
						</div>

						<div className="space-y-2">
							<Label>Tipo de hora</Label>
							<Button
								variant="outline"
								className="w-full justify-between font-normal"
								onClick={() => setShowHourTypeSearch(true)}
								disabled={isDisabled}
							>
								<span className={selectedHourType ? '' : 'text-muted-foreground'}>
									{selectedHourType
										? `${selectedHourType.name}${selectedHourType.code ? ` (${selectedHourType.code})` : ''}`
										: 'Selecciona un tipo de hora'}
								</span>
								<ChevronRight className="h-4 w-4 shrink-0 opacity-50" />
							</Button>
						</div>
						<div className="space-y-2">
							<Label>Proyecto</Label>
							<Button
								variant="outline"
								className="w-full justify-between font-normal"
								onClick={() => setShowProjectSearch(true)}
								disabled={isDisabled}
							>
								<span className={selectedProject ? '' : 'text-muted-foreground'}>
									{selectedProject
										? `${selectedProject.name}${selectedProject.code ? ` (${selectedProject.code})` : ''}`
										: 'Selecciona un proyecto'}
								</span>
								<ChevronRight className="h-4 w-4 shrink-0 opacity-50" />
							</Button>
						</div>
						<div className="space-y-2">
							<Label htmlFor="text">Comentarios</Label>
							<Textarea
								id="text"
								value={text}
								onChange={(e) => setText(e.target.value)}
								placeholder="Añade comentarios"
								rows={4}
								disabled={isDisabled}
							/>
						</div>
						{line?.notes && (
							<div className="space-y-2">
								<Button
									type="button"
									variant="outline"
									onClick={() => setShowHistoryDialog(true)}
									disabled={saving || deleting}
									className="w-full"
								>
									<History className="h-4 w-4 mr-2" />
									Mostrar historial de cambios
								</Button>
							</div>
						)}
					</div>
					<DialogFooter className="flex-col sm:flex-row gap-2 flex-shrink-0">
						<div className="flex gap-2 w-full sm:w-auto flex-1 justify-end">
							{!isApproved && (
								<Button
									variant="default"
									onClick={handleApprove}
									disabled={saving || deleting || approving}
									className="w-full sm:w-auto"
								>
									<CheckCircle2 className="h-4 w-4 mr-2" />
									{approving ? 'Aprobando...' : 'Aprobar'}
								</Button>
							)}
							<Button
								variant="outline"
								onClick={() => onOpenChange(false)}
								disabled={saving || deleting || approving}
							>
								Cancelar
							</Button>
							{!isApproved && (
								<Button onClick={handleSave} disabled={saving || deleting || approving}>
									{saving ? 'Guardando...' : 'Guardar'}
								</Button>
							)}
						</div>
					</DialogFooter>
				</DialogContent>
			</Dialog>
			<DeleteConfirmDialog
				open={showDeleteConfirm}
				onOpenChange={setShowDeleteConfirm}
				title="¿Estás seguro?"
				description="Esta acción no se puede deshacer. Se eliminará permanentemente esta línea del registro horario."
				onConfirm={handleDelete}
			/>
			<SearchableSelectDialog
				open={showHourTypeSearch}
				onOpenChange={setShowHourTypeSearch}
				title="Seleccionar tipo de hora"
				items={hourTypes}
				selectedId={hourTypeID}
				onSelect={setHourTypeID}
			/>
			<SearchableSelectDialog
				open={showProjectSearch}
				onOpenChange={setShowProjectSearch}
				title="Seleccionar proyecto"
				items={projects}
				selectedId={projectID}
				onSelect={setProjectID}
			/>
			<Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
				<DialogContent className="max-w-md max-h-[90vh] flex flex-col">
					<DialogHeader>
						<DialogTitle>Historial de cambios</DialogTitle>
						<DialogDescription>Registro de modificaciones realizadas en esta línea</DialogDescription>
					</DialogHeader>
					<div className="py-4 overflow-y-auto flex-1 min-h-0">
						<div className="rounded-md border bg-muted/50 p-4 text-sm text-muted-foreground whitespace-pre-wrap">
							{line?.notes || ''}
						</div>
					</div>
					<DialogFooter className="flex-shrink-0">
						<Button variant="outline" onClick={() => setShowHistoryDialog(false)}>
							Cerrar
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	)
}
